import { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

import Autocomplete from '@mui/material/Autocomplete';
import { CircularProgress, TextField } from '@mui/material';

import { fetchSearchedContacts, resetSearchedContacts } from 'store/actions/crm';
import { crmSelectors } from 'store/selectors/crm';
import { trackEvent } from 'utils/analytics';

import { SearchResultOption } from 'common/components';

const MIN_SEARCH_LENGTH = 3;

const SEARCH_CONTEXT = 'contact_selection';

const contactLabel = contact => `${contact.firstName || ''} ${contact.lastName || ''}`.trim();

const contactKey = contact => contact.contact || contact.uuid;

const contactAddress = contact => {
  const location = contact.locations?.[0];

  if (!location) {
    return '';
  }

  return [
    location.streetAddress,
    location.city,
    [location.state, location.zipCode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
};

/**
 * Redux-connected multi-select Autocomplete for picking CRM contacts by name.
 * Typing three or more characters triggers a debounced server-side search
 * (`fetchSearchedContacts`); results render via `SearchResultOption` with the
 * contact's first location as the metadata line. Controlled through
 * `contacts`/`setContacts` — use wherever a form needs one or more existing
 * contacts attached.
 */
const ContactsSelection = ({ contacts, organizationUuid, setContacts }) => {
  const dispatch = useDispatch();

  const { isSearching, searchedContacts } = useSelector(state => ({
    isSearching: crmSelectors.isSearching(state),
    searchedContacts: crmSelectors.getSearchedContacts(state),
  }));

  const [searchedPhrase, setSearchedPhrase] = useState('');

  // Tracks whether `search_initiated` has fired for the current search, so
  // that refining a phrase reports one search rather than one per keystroke.
  const hasTrackedSearch = useRef(false);

  const debouncedFetchSearchedContacts = useCallback(
    debounce(phrase => {
      if (!hasTrackedSearch.current) {
        hasTrackedSearch.current = true;
        trackEvent('search_initiated', {
          search_context: SEARCH_CONTEXT,
          search_type: 'contact',
        });
      }

      dispatch(fetchSearchedContacts(phrase, organizationUuid));
    }, 500),
    [organizationUuid]
  );

  return (
    <Autocomplete
      fullWidth
      multiple
      filterSelectedOptions
      getOptionLabel={contactLabel}
      // https://github.com/mui/material-ui/issues/20068
      filterOptions={x => x}
      inputValue={searchedPhrase}
      isOptionEqualToValue={(option, value) => contactKey(option) === contactKey(value)}
      loading={isSearching}
      noOptionsText={
        searchedPhrase.length < MIN_SEARCH_LENGTH
          ? `Type at least ${MIN_SEARCH_LENGTH} characters to search`
          : 'No matching contacts'
      }
      onChange={(_, newValue, reason, details) => {
        if (reason === 'selectOption' && details?.option) {
          trackEvent('search_result_selected', {
            query_length: searchedPhrase.length,
            result_count: searchedContacts.length,
            result_index: searchedContacts.findIndex(
              option => contactKey(option) === contactKey(details.option)
            ),
            search_context: SEARCH_CONTEXT,
            search_type: 'contact',
          });
          hasTrackedSearch.current = false;
        }

        setContacts(newValue);
      }}
      onInputChange={(_, newValue) => {
        if (newValue.length >= MIN_SEARCH_LENGTH) {
          debouncedFetchSearchedContacts(newValue);
        } else {
          debouncedFetchSearchedContacts.cancel();
          dispatch(resetSearchedContacts());
        }

        if (!newValue) {
          hasTrackedSearch.current = false;
        }

        setSearchedPhrase(newValue);
      }}
      options={searchedContacts}
      renderOption={(props, option) => (
        <SearchResultOption
          key={contactKey(option)}
          meta={contactAddress(option)}
          name={contactLabel(option)}
          optionProps={props}
        />
      )}
      renderInput={params => (
        <TextField
          {...params}
          label="Search contacts"
          placeholder={contacts.length ? '' : 'Search your contacts by name'}
          variant="outlined"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      value={contacts}
    />
  );
};

ContactsSelection.propTypes = {
  /** Currently selected contacts (the controlled Autocomplete value). */
  contacts: PropTypes.arrayOf(
    PropTypes.shape({
      contact: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      uuid: PropTypes.string,
    })
  ),
  organizationUuid: PropTypes.string.isRequired,
  setContacts: PropTypes.func.isRequired,
};

ContactsSelection.defaultProps = {
  contacts: [],
};

export default ContactsSelection;
